import { createContext, useState, ReactNode, FC, useEffect } from "react";
import { CurrencyType } from "../types/types";

interface CurrencyContextType {
  currency: CurrencyType;
  setCurrency: (currency: CurrencyType) => void;
}

export const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined
);

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: FC<CurrencyProviderProps> = ({ children }) => {
  const [currency, setCurrency] = useState<CurrencyType>(() => {
    if (typeof window !== "undefined") {
      const storedCurrency = localStorage.getItem("preferred-currency");
      if (storedCurrency === "BRL" || storedCurrency === "USD") {
        return storedCurrency as CurrencyType;
      }

      // Check URL params
      const params = new URLSearchParams(window.location.search);
      const urlCurrency = params.get("currency");
      if (urlCurrency === "BRL") {
        return "BRL";
      }
    }
    return "USD";
  });

  // React to URL changes
  useEffect(() => {
    // Only execute this effect on the client side
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const urlCurrency = params.get("currency");

    if (urlCurrency === "BRL" && currency !== "BRL") {
      setCurrency("BRL");
    } else if (urlCurrency === "USD" && currency !== "USD") {
      setCurrency("USD");
    }

    // Listen for URL changes (popstate event)
    const handleUrlChange = () => {
      const newParams = new URLSearchParams(window.location.search);
      const newUrlCurrency = newParams.get("currency");

      if (newUrlCurrency === "BRL" && currency !== "BRL") {
        setCurrency("BRL");
      } else if (newUrlCurrency === "USD" && currency !== "USD") {
        setCurrency("USD");
      }
    };

    // Add event listener for history changes
    window.addEventListener("popstate", handleUrlChange);

    // Clean up
    return () => {
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, [currency]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("preferred-currency", currency);
    }
  }, [currency]);

  // One-time check for IP location ONLY if we don't already have a preference
  useEffect(() => {
    const checkLocationOnce = async () => {
      // Skip if we already have a stored preference or URL param
      if (typeof window !== "undefined") {
        const hasStoredPreference = localStorage.getItem("preferred-currency");
        const params = new URLSearchParams(window.location.search);
        const hasUrlParam = params.has("currency");

        if (!hasStoredPreference && !hasUrlParam) {
          try {
            const response = await fetch("https://ipapi.co/json/");
            const data = await response.json();

            if (data.country_code === "BR") {
              setCurrency("BRL");
            }
          } catch (error) {
            console.error("Failed to fetch user location:", error);
          }
        }
      }
    };

    checkLocationOnce();
  }, []);

  const handleSetCurrency = (newCurrency: CurrencyType) => {
    setCurrency(newCurrency);

    if (typeof window !== "undefined" && window.history && window.location) {
      const url = new URL(window.location.href);
      url.searchParams.set("currency", newCurrency);
      window.history.replaceState({}, "", url.toString());
    }
  };

  const value = {
    currency,
    setCurrency: handleSetCurrency,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
